/**
 * 🎨 ProgressCard - Affiliate Level Progress
 * 
 * Zeigt den Fortschritt zum nächsten Affiliate Level:
 * - Aktuelles Level mit Badge
 * - Progress Bar
 * - Aktuelle/Benötigte Werte
 * - Bonus-Info für nächstes Level
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Gift, Star } from 'lucide-react';

// ============================================
// TYPES
// ============================================

export type AffiliateLevel = 'starter' | 'bronze' | 'silber' | 'gold' | 'platin' | 'diamant';

export interface ProgressCardProps {
  /** Aktuelles Level */
  level: AffiliateLevel;
  /** Nächstes Level (null wenn bereits Platin) */
  nextLevel?: AffiliateLevel | null;
  /** Aktueller Fortschritt (0-100) */
  progress: number;
  /** Aktueller Wert */
  currentValue: number;
  /** Benötigter Wert für nächstes Level */
  requiredValue: number;
  /** Einheit für die Werte */
  unit: string;
  /** Bonus-Info für nächstes Level */
  nextLevelBonus?: {
    percentage: number;
    label: string;
  };
  /** Zusätzliche CSS Klassen */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const LEVEL_CONFIG: Record<AffiliateLevel, {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  progressColor: string;
  commission: number;
}> = {
  starter: {
    label: 'Starter',
    icon: '🌱',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    progressColor: 'bg-green-500',
    commission: 3,
  },
  bronze: {
    label: 'Bronze',
    icon: '🥉',
    color: 'from-amber-600 to-amber-700',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    progressColor: 'bg-amber-500',
    commission: 5,
  },
  silber: {
    label: 'Silber',
    icon: '🥈',
    color: 'from-gray-400 to-gray-500',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    progressColor: 'bg-gray-400',
    commission: 8,
  },
  gold: {
    label: 'Gold',
    icon: '🥇',
    color: 'from-yellow-400 to-yellow-500',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    progressColor: 'bg-yellow-500',
    commission: 12,
  },
  platin: {
    label: 'Platin',
    icon: '💎',
    color: 'from-cyan-400 to-cyan-500',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-200',
    progressColor: 'bg-cyan-500',
    commission: 15,
  },
  diamant: {
    label: 'Diamant',
    icon: '💠',
    color: 'from-purple-400 to-purple-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
    progressColor: 'bg-purple-500',
    commission: 20,
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface LevelBadgeInlineProps {
  level: AffiliateLevel;
  size?: 'sm' | 'md' | 'lg';
}

const LevelBadgeInline: React.FC<LevelBadgeInlineProps> = ({ level, size = 'md' }) => {
  const config = LEVEL_CONFIG[level];
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full font-medium',
      sizeClasses[size],
      config.bgColor,
      config.textColor
    )}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ProgressCard: React.FC<ProgressCardProps> = ({
  level,
  nextLevel,
  progress,
  currentValue,
  requiredValue,
  unit,
  nextLevelBonus,
  className,
}) => {
  const currentConfig = LEVEL_CONFIG[level];
  const nextConfig = nextLevel ? LEVEL_CONFIG[nextLevel] : null;
  const isMaxLevel = !nextLevel;

  // Formatiere Werte
  const formatValue = (value: number) => {
    if (unit.includes('€')) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('de-DE').format(value);
  };

  return (
    <div className={cn(
      'bg-white/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 md:p-6',
      'shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
      'border border-white/60',
      'relative overflow-hidden',
      className
    )}>
      {/* Gradient Accent */}
      <div className="absolute -top-10 -right-10 w-32 sm:w-40 h-32 sm:h-40 bg-gradient-to-bl from-purple-500/15 to-transparent rounded-full" />
      
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-5 relative gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={cn(
            'w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0',
            `bg-gradient-to-br ${currentConfig.color}`
          )}>
            <span className="text-xl sm:text-2xl">{currentConfig.icon}</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent truncate">
              {currentConfig.label} Level
            </h3>
            <p className="text-xs sm:text-sm text-gray-500">
              {currentConfig.commission}% Provision
            </p>
          </div>
        </div>
        
        {nextConfig && (
          <div className="text-right flex-shrink-0">
            <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Nächstes Level</p>
            <LevelBadgeInline level={nextLevel!} size="sm" />
          </div>
        )}
      </div>

      {/* Progress Section */}
      {!isMaxLevel ? (
        <>
          {/* Progress Bar */}
          <div className="mb-2 sm:mb-3 relative">
            <div className="flex justify-between items-center mb-1.5 sm:mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                Fortschritt
              </span>
              <span className="text-xs sm:text-sm font-bold bg-gradient-to-r from-emerald-600 to-purple-600 bg-clip-text text-transparent">
                {progress.toFixed(0)}%
              </span>
            </div>
            
            <div className="h-2.5 sm:h-3 bg-gray-100/80 rounded-full overflow-hidden shadow-inner">
              <div 
                className={cn(
                  'h-full rounded-full transition-colors duration-500 ease-out',
                  'bg-gradient-to-r from-emerald-500 via-teal-500 to-purple-500',
                  'shadow-sm'
                )}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>

          {/* Values */}
          <div className="flex items-center justify-between text-xs sm:text-sm mb-3 sm:mb-4">
            <span className="text-gray-600 font-medium">
              {formatValue(currentValue)} {!unit.includes('€') && unit}
            </span>
            <span className="text-gray-300">/</span>
            <span className="font-semibold bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-transparent">
              {formatValue(requiredValue)} {!unit.includes('€') && unit}
            </span>
          </div>

          {/* Next Level Bonus */}
          {nextLevelBonus && (
            <div className={cn(
              'p-3 sm:p-4 rounded-lg',
              'bg-gradient-to-r from-primary/5 to-primary/10',
              'border border-primary/20'
            )}>
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-0.5">
                    {nextConfig?.label} Bonus
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 leading-tight">
                    {nextLevelBonus.percentage}% {nextLevelBonus.label}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Max Level Reached */
        <div className={cn(
          'p-4 rounded-lg text-center',
          'bg-gradient-to-r from-cyan-50 to-cyan-100',
          'border border-cyan-200'
        )}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Star className="w-5 h-5 text-cyan-600" />
            <span className="font-semibold text-cyan-700">Höchstes Level erreicht!</span>
            <Star className="w-5 h-5 text-cyan-600" />
          </div>
          <p className="text-sm text-cyan-600">
            Du profitierst von der maximalen Provision von {currentConfig.commission}%
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// LEVEL BADGE STANDALONE
// ============================================

export interface LevelBadgeProps {
  level: AffiliateLevel;
  showCommission?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LevelBadge: React.FC<LevelBadgeProps> = ({
  level,
  showCommission = false,
  size = 'md',
  className,
}) => {
  const config = LEVEL_CONFIG[level];
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-3 py-1 text-sm gap-1.5',
    lg: 'px-4 py-1.5 text-base gap-2',
  };

  return (
    <span className={cn(
      'inline-flex items-center rounded-full font-medium',
      sizeClasses[size],
      config.bgColor,
      config.textColor,
      className
    )}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
      {showCommission && (
        <span className="opacity-75">({config.commission}%)</span>
      )}
    </span>
  );
};

export default ProgressCard;
